import React, { createContext, type FC, type PropsWithChildren, useContext } from 'react'

import type { AbsoluteRoutePathWithAnyId } from '../../routing'
import { useRecordPanelContent } from './Content'

/** The details component of each details route, as the router renders it */
type RecordPanelDetails = Readonly<Partial<Record<AbsoluteRoutePathWithAnyId, FC>>>

const RecordPanelDetailsContext = createContext<RecordPanelDetails>({})

/**
 * What a table's row opens in a record panel, by the route the row would navigate to; a route without details navigates
 */
export const RecordPanelDetailsProvider: FC<Readonly<{ details: RecordPanelDetails }> & PropsWithChildren> = ({ details, children }) => (
    <RecordPanelDetailsContext.Provider value={details}>{children}</RecordPanelDetailsContext.Provider>
)

/**
 * @returns the details to open in a record panel for the given route, `null` to navigate instead
 *
 * Inside a panel, tables navigate, so only one panel is ever open: its lists are of the types under its record's,
 * never of the type of the list it was opened from, whose page subscription can't run twice
 */
export const useRecordPanelDetails = (route: AbsoluteRoutePathWithAnyId | null): FC | null => {
    const details = useContext(RecordPanelDetailsContext)
    const inPanel = useRecordPanelContent() !== null

    if (route === null || inPanel) {
        return null
    }

    return details[route] ?? null
}
